import { useEffect, useState } from 'react'
import {
  CloseButton,
  NativeSelect,
  Input,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Button,
  IconButton,
  Menu,
  Field,
  Portal,
  Dialog,
  Switch,
} from '@chakra-ui/react';
import { FiMoreVertical, FiTrash2, FiUserMinus, FiPlus, FiFileText, FiStar } from 'react-icons/fi'
import { ResponsiveModal, StatusChip } from '../../shared'
import { extractApiMessage } from '../../../lib/apiError'
import {
  DAY_START_MINUTES, DAY_END_MINUTES, SLOT_MINUTES, minutesToTime, timeToMinutes, formatSlotTime, isNewId,
} from './lobbyScheduleUtils'
import { clampEndToDay } from './timelineLayout'
import type {
  AttendanceStatus, ConstituencyStatus, LobbyDayAskDto, LobbyMeetingStatus, MeetingRole, MeetingWith,
  ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'

type ErrorOpts = { onError?: (err: unknown) => void }

const CONSTITUENCY: Record<ConstituencyStatus, { label: string; color: string }> = {
  Constituent: { label: 'constituent', color: 'green' },
  NotConstituent: { label: 'not in district', color: 'red' },
  Unknown: { label: 'unverified', color: 'yellow' },
}

const STATUS_OPTIONS: { value: LobbyMeetingStatus; label: string }[] = [
  { value: 'Tentative', label: 'Tentative' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'DropOff', label: 'Drop off materials' },
]

const WITH_WHOM_OPTIONS: { value: MeetingWith; label: string }[] = [
  { value: 'Unknown', label: 'Not sure' },
  { value: 'Legislator', label: 'Legislator' },
  { value: 'Staff', label: 'Staff' },
  { value: 'Both', label: 'Legislator + staff' },
]

function shortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface MeetingDetailSheetProps {
  isOpen: boolean
  onClose: () => void
  meeting: ScheduleMeetingDto | null
  legislator: ScheduleLegislatorRowDto | undefined
  allMeetings: ScheduleMeetingDto[]
  legislatorById: Map<string, ScheduleLegislatorRowDto>
  onUnassign: (assignmentId: string) => void
  onMove: (assignmentId: string, toMeetingId: string, attendeeId: string) => void
  onAddAttendee: () => void
  onDelete: (meetingId: string) => void
  onReschedule: (meetingId: string, startTime: string, endTime: string) => void
  /** Optional — when provided, a "Preview packet" action opens the organizer packet.
   *  Omitted on surfaces without a packet drawer (e.g. the mobile agenda editor). */
  onOpenPacket?: (meetingId: string) => void
  /** Optional — when provided (together with onSetAsk), an Ask picker is shown. */
  asks?: LobbyDayAskDto[]
  /** Optional — when provided, the Ask picker is shown and edits fire this callback. */
  onSetAsk?: (meetingId: string, askId: string | null) => void
  /** Optional — when provided, a Room input is shown and edits fire this callback. */
  onSetRoom?: (meetingId: string, room: string) => void
  /** Optional — when provided, a meeting-status selector is shown. */
  onSetStatus?: (meetingId: string, status: LobbyMeetingStatus) => void
  /** Optional — when provided, per-row Late + No-show toggles + header attendance count are shown. */
  onSetAttendance?: (meetingId: string, assignmentId: string, status: AttendanceStatus) => void
  /** Optional — when provided, a "Meeting with" selector is shown (legislator → staffer swap). */
  onSetWithWhom?: (meetingId: string, withWhom: MeetingWith) => void
  /** Optional — when provided, each row's menu offers Make / Remove as team lead and the lead's
   *  row carries a "Lead" chip. A server failure is reported through `opts.onError`. */
  onSetRole?: (meetingId: string, assignmentId: string, role: MeetingRole, opts?: ErrorOpts) => void
  /** The viewer's user id — marks their own staff tag " (you)" and drives the toggle state. */
  currentUserId?: string
  /** Optional — when provided, the staff line is shown; with `currentUserId`, so is the
   *  "I'm in this meeting" switch. A server failure is reported through `opts.onError`. */
  onSetMyStaffTag?: (meetingId: string, on: boolean, opts?: ErrorOpts) => void
}

export function MeetingDetailSheet({
  isOpen, onClose, meeting, legislator, allMeetings, legislatorById,
  onUnassign, onMove, onAddAttendee, onDelete, onReschedule, onOpenPacket,
  asks, onSetAsk, onSetRoom, onSetStatus, onSetAttendance,
  onSetWithWhom, onSetRole, currentUserId, onSetMyStaffTag,
}: MeetingDetailSheetProps) {
  const [moveFor, setMoveFor] = useState<{ assignmentId: string; attendeeId: string } | null>(null)
  // Out-of-band write failures, keyed by meeting so one meeting's error never shows on another's sheet.
  const [roleError, setRoleError] = useState<{ meetingId: string; err: unknown } | null>(null)
  const [staffError, setStaffError] = useState<{ meetingId: string; err: unknown } | null>(null)
  // A stale error must not reappear when the sheet is reopened or switched to another meeting.
  const sheetMeetingId = meeting?.id
  useEffect(() => {
    setRoleError(null)
    setStaffError(null)
  }, [isOpen, sheetMeetingId])

  if (!meeting) return null
  // Staged (unsaved) meetings/rows carry temporary `new:` ids the out-of-band PUTs cannot route,
  // so their immediate controls are replaced by a hint until Save.
  const isStagedMeeting = isNewId(meeting.id)
  const staff = meeting.staff ?? []
  const isTaggedMe = !!currentUserId && staff.some((s) => s.userId === currentUserId)
  const roleErr = roleError?.meetingId === meeting.id ? roleError.err : null
  const staffErr = staffError?.meetingId === meeting.id ? staffError.err : null
  const changeRole = (assignmentId: string, role: MeetingRole) => {
    if (!onSetRole) return
    const meetingId = meeting.id
    setRoleError(null)
    onSetRole(meetingId, assignmentId, role, { onError: (err) => setRoleError({ meetingId, err }) })
  }
  const toggleMyStaffTag = (on: boolean) => {
    if (!onSetMyStaffTag) return
    const meetingId = meeting.id
    setStaffError(null)
    onSetMyStaffTag(meetingId, on, { onError: (err) => setStaffError({ meetingId, err }) })
  }
  const moveTargets = allMeetings.filter((m) => m.id !== meeting.id)

  const slotValues = (() => {
    const v: string[] = []
    for (let t = DAY_START_MINUTES; t < DAY_END_MINUTES; t += SLOT_MINUTES) v.push(minutesToTime(t))
    return v
  })()
  const currentDuration = timeToMinutes(meeting.endTime) - timeToMinutes(meeting.startTime)
  const durationOptions = [30, 45, 60, 90]

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          borderTopRadius={{ base: 'xl', md: 'md' }}
          maxH="85vh"
          pb="env(safe-area-inset-bottom, 0px)">
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Header borderBottomWidth="1px">
            <VStack align="stretch" gap={0}>
              <Text lineClamp={1}>{legislator?.fullName ?? 'Unknown legislator'}</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.500">{meeting.room ?? 'No room set'}</Text>
            </VStack>
          </Dialog.Header>
          <Dialog.Body>
            <HStack gap={3} align="flex-end" mb={3}>
              <Field.Root>
                <Field.Label fontSize="sm" htmlFor="detail-start">Start time</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    id="detail-start"
                    aria-label="Start time"
                    value={meeting.startTime}
                    onChange={(e) => onReschedule(meeting.id, e.target.value, clampEndToDay(e.target.value, currentDuration))}>
                    {slotValues.map((v) => <option key={v} value={v}>{formatSlotTime(v)}</option>)}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm" htmlFor="detail-duration">Duration</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    id="detail-duration"
                    aria-label="Duration"
                    value={String(currentDuration)}
                    onChange={(e) => onReschedule(meeting.id, meeting.startTime, clampEndToDay(meeting.startTime, Number(e.target.value)))}>
                    {durationOptions.map((d) => <option key={d} value={d}>{d} min</option>)}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            </HStack>
            {onSetStatus && (
              <Field.Root mb={3}>
                <Field.Label fontSize="sm" htmlFor="detail-status">Status</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    id="detail-status"
                    aria-label="Status"
                    value={meeting.status}
                    onChange={(e) => onSetStatus(meeting.id, e.target.value as LobbyMeetingStatus)}>
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            )}
            {onSetWithWhom && (
              <Field.Root mb={3}>
                <Field.Label fontSize="sm" htmlFor="detail-with-whom">Meeting with</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    id="detail-with-whom"
                    aria-label="Meeting with"
                    value={meeting.withWhom}
                    onChange={(e) => onSetWithWhom(meeting.id, e.target.value as MeetingWith)}>
                    {WITH_WHOM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            )}
            {onSetAsk && (
              <Field.Root mb={3}>
                <Field.Label fontSize="sm" htmlFor="detail-ask">Ask</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    id="detail-ask"
                    aria-label="Ask"
                    placeholder="No ask"
                    value={meeting.askId ?? ''}
                    onChange={(e) => onSetAsk(meeting.id, e.target.value || null)}>
                    {(asks ?? []).map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            )}
            {onSetRoom && (
              <Field.Root mb={3}>
                <Field.Label fontSize="sm" htmlFor="detail-room">Room</Field.Label>
                <Input
                  id="detail-room" aria-label="Room" size="sm" placeholder="e.g. Rm 214"
                  value={meeting.room ?? ''}
                  onChange={(e) => onSetRoom(meeting.id, e.target.value)}
                />
              </Field.Root>
            )}
            {onSetMyStaffTag && (
              <VStack align="stretch" gap={1} mb={3}>
                {staff.length > 0 && (
                  <Text fontSize="sm" color="fg.muted">
                    Staff in this meeting: {staff
                      .map((s) => (s.userId === currentUserId ? `${s.displayName} (you)` : s.displayName))
                      .join(', ')}
                  </Text>
                )}
                {currentUserId && isStagedMeeting && (
                  <Text fontSize="sm" color="fg.muted">Save changes first</Text>
                )}
                {currentUserId && !isStagedMeeting && (
                  <Switch.Root
                    checked={isTaggedMe}
                    onCheckedChange={(e) => toggleMyStaffTag(e.checked === true)}
                    colorPalette="brand"
                    minH="44px"
                    display="flex"
                    alignItems="center"
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label fontSize="sm">I&apos;m in this meeting</Switch.Label>
                  </Switch.Root>
                )}
                {!!staffErr && (
                  <Text fontSize="sm" color="danger.fg">{extractApiMessage(staffErr, 'Could not update staff')}</Text>
                )}
              </VStack>
            )}
            {onSetAttendance && meeting.assignments.length > 0 && (() => {
              const total = meeting.assignments.length
              const noShows = meeting.assignments.filter((a) => a.attendance === 'Absent').length
              const late = meeting.assignments.filter((a) => a.attendance === 'Late').length
              // Late counts as attending (DB-5); a zero no-show segment is omitted ("1 late · 3 attending").
              const parts = [
                ...(noShows > 0 ? [`${noShows} no-show${noShows === 1 ? '' : 's'}`] : []),
                ...(late > 0 ? [`${late} late`] : []),
                `${total - noShows} attending`,
              ]
              return (
                <HStack mb={1}>
                  {noShows === 0 && late === 0 ? (
                    <StatusChip variant="success">All {total} attending</StatusChip>
                  ) : (
                    <StatusChip variant={noShows > 0 ? 'danger' : 'warning'}>{parts.join(' · ')}</StatusChip>
                  )}
                </HStack>
              )
            })()}
            {!!roleErr && (
              <Text fontSize="sm" color="danger.fg" mb={1}>{extractApiMessage(roleErr, 'Could not update the team lead')}</Text>
            )}
            <VStack gap={2} align="stretch" py={2}>
              {meeting.assignments.length === 0 && (
                <Text fontSize="sm" color="gray.400">No attendees assigned yet</Text>
              )}
              {meeting.assignments.map((a) => {
                const badge = CONSTITUENCY[a.constituency]
                const isMoving = moveFor?.assignmentId === a.assignmentId
                const isAbsent = a.attendance === 'Absent'
                const isLate = a.attendance === 'Late'
                const isLead = a.role === 'Lead'
                const isStaged = isNewId(a.assignmentId)
                return (
                  <Box key={a.assignmentId} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
                    {/* Wraps so the Late / No-show / menu controls drop below the name at 390px
                        instead of squeezing it — no JS fork needed for a simple reflow. */}
                    <HStack justify="space-between" flexWrap="wrap" rowGap={1}>
                      <HStack gap={2} minW={0} flex="1 1 140px">
                        <VStack align="flex-start" gap={0} minW={0}>
                          <Text
                            fontSize="sm"
                            lineClamp={1}
                            color={isAbsent ? 'fg.muted' : undefined}
                            textDecoration={isAbsent ? 'line-through' : undefined}
                          >{a.fullName}</Text>
                          {isAbsent && <Text fontSize="2xs" color="danger.fg">No-show</Text>}
                          {isLate && <Text fontSize="2xs" color="warning.fg">Late</Text>}
                        </VStack>
                        {onSetRole && isLead && <StatusChip variant="info">Lead</StatusChip>}
                        <Badge colorPalette={badge.color} fontSize="2xs" flexShrink={0}>{badge.label}</Badge>
                      </HStack>
                      <HStack gap={1} flexShrink={0} ml="auto">
                        {isStaged && (onSetAttendance || onSetRole) && (
                          <Text fontSize="sm" color="fg.muted">Save changes first</Text>
                        )}
                        {onSetAttendance && !isStaged && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="gray"
                            // Late takes the warning status tokens (StatusChip's warning idiom).
                            {...(isLate ? { bg: 'warning.subtle', color: 'warning.fg', borderColor: 'warning.fg' } : {})}
                            minH="44px"
                            aria-pressed={isLate}
                            aria-label={isLate ? `Undo late for ${a.fullName}` : `Mark ${a.fullName} as late`}
                            onClick={() => onSetAttendance(meeting.id, a.assignmentId, isLate ? 'Unmarked' : 'Late')}
                          >
                            Late
                          </Button>
                        )}
                        {onSetAttendance && !isStaged && (
                          <Button
                            size="sm"
                            variant={isAbsent ? 'solid' : 'outline'}
                            colorPalette={isAbsent ? 'red' : 'gray'}
                            minH="44px"
                            aria-pressed={isAbsent}
                            aria-label={isAbsent ? `Undo no-show for ${a.fullName}` : `Mark ${a.fullName} as a no-show`}
                            onClick={() => onSetAttendance(meeting.id, a.assignmentId, isAbsent ? 'Unmarked' : 'Absent')}
                          >
                            No-show
                          </Button>
                        )}
                        <Menu.Root>
                        <Menu.Trigger asChild><IconButton
                            aria-label={`Actions for ${a.fullName}`}
                            size="sm"
                            variant="ghost"
                            minW="44px"
                            h="44px"><FiMoreVertical /></IconButton></Menu.Trigger>
                        <Portal><Menu.Positioner><Menu.Content>
                              <Menu.Item
                                aria-label={`Unassign ${a.fullName}`}
                                onSelect={() => onUnassign(a.assignmentId)}
                                value='item-0'>
                                <FiUserMinus />
                                Unassign
                              </Menu.Item>
                              {moveTargets.length > 0 && (
                                <Menu.Item
                                  aria-label="Move to another meeting"
                                  onSelect={() => setMoveFor(isMoving ? null : { assignmentId: a.assignmentId, attendeeId: a.attendeeId })}
                                  value='item-1'>
                                  Move to…
                                </Menu.Item>
                              )}
                              {onSetRole && !isStaged && (
                                <Menu.Item
                                  onSelect={() => changeRole(a.assignmentId, isLead ? 'Member' : 'Lead')}
                                  value='item-2'>
                                  <FiStar />
                                  {isLead ? 'Remove as team lead' : 'Make team lead'}
                                </Menu.Item>
                              )}
                            </Menu.Content></Menu.Positioner></Portal>
                        </Menu.Root>
                      </HStack>
                    </HStack>
                    {isMoving && (
                      <VStack align="stretch" gap={1} mt={2} pl={1}>
                        {moveTargets.map((t) => (
                          <Button
                            key={t.id} size="xs" variant="outline" justifyContent="flex-start"
                            onClick={() => { onMove(a.assignmentId, t.id, a.attendeeId); setMoveFor(null) }}
                          >
                            {legislatorById.get(t.legislatorId)?.fullName ?? 'Meeting'} · {t.startTime.slice(0, 5)}
                            {t.meetingDate !== meeting.meetingDate ? ` · ${shortDate(t.meetingDate)}` : ''}
                          </Button>
                        ))}
                      </VStack>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer borderTopWidth="1px">
            <HStack justify="space-between" w="full">
              <HStack gap={1}>
                <IconButton
                  aria-label="Delete meeting"
                  variant="ghost"
                  colorPalette="red"
                  minW="44px"
                  h="44px"
                  onClick={() => { onDelete(meeting.id); onClose() }}><FiTrash2 /></IconButton>
                {onOpenPacket && (
                  <Button variant="ghost" minH="44px" onClick={() => onOpenPacket(meeting.id)}><FiFileText />Preview packet
                                    </Button>
                )}
              </HStack>
              <Button colorPalette="brand" minH="44px" onClick={onAddAttendee}><FiPlus />Add attendee</Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
